import { useEffect, useRef } from 'react'
import { shouldLoadTagManager } from '#app/utils/pz/analytics/consentGate'
import {
	loadTagManager,
	pushConsentUpdate,
} from '#app/utils/pz/analytics/tagManagerLoader'

export interface EnvVariables {
	MODE?: 'production' | 'development' | 'test'
	SENTRY_DSN?: string
	ALLOW_INDEXING?: 'true' | 'false'
	TAG_MANAGER_ID?: string
}

/**
 * Hook for Google Tag Manager. Decoupled from the consent decision: the hook
 * asks {@link shouldLoadTagManager} whether the script may be loaded and, only
 * if so, delegates to {@link loadTagManager}. With consent rejected or unknown
 * (`consentGiven === false`), `gtm.js` is not requested.
 */
export function useTagManager(
	tagManagerId: string | undefined,
	consentGiven: boolean,
	options?: {
		dataLayer?: Record<string, unknown>
		enableConsentMode?: boolean
		enabled?: boolean
	},
) {
	const lastConsentRef = useRef<boolean | null>(null)
	const { dataLayer, enableConsentMode = false, enabled = true } = options ?? {}

	useEffect(() => {
		if (!enabled) return
		if (!tagManagerId?.length) return
		if (process.env.NODE_ENV !== 'production') return

		const consentState = consentGiven ? 'granted' : 'rejected'
		if (!shouldLoadTagManager(consentState)) return

		void loadTagManager({
			id: tagManagerId,
			dataLayer,
			enableConsentMode,
		})
	}, [tagManagerId, consentGiven, dataLayer, enableConsentMode, enabled])

	useEffect(() => {
		if (!enabled) return
		if (!tagManagerId?.length) return
		if (process.env.NODE_ENV !== 'production') return
		if (lastConsentRef.current === consentGiven) return
		lastConsentRef.current = consentGiven

		pushConsentUpdate(consentGiven, enableConsentMode)
	}, [tagManagerId, consentGiven, enableConsentMode, enabled])
}
